
import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    TextField,
    Box,
    Typography,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
} from "@mui/material";
import { Iconography, Button, Loader, LoaderSizes, IconographySizes, IconographyThemes } from "@hid-galaxy-ui/galaxy-react";
import { AddPartnerRegionalMultiplierDetailsType } from "../../../../../../../redux/saga/partner-program/regional-multipliers/saga-actions";
import { usePartnerForm } from "../../../../../../../hooks/usePartnerForm";
import { useAppSelector } from "../../../../../../../redux";

type Option = { label: string; value: string };

type Props = {
    onClose: () => void;
    onSubmit: (payload: AddPartnerRegionalMultiplierDetailsType) => void;
    open?: boolean;
    mode?: "add" | "edit";
    initialValue?: Partial<AddPartnerRegionalMultiplierDetailsType>;
    busy?: boolean;
    countries?: Option[];
    regions?: Option[];
    codes?: Option[];
};

const fieldSx = {
    "& .MuiFilledInput-root": {
        backgroundColor: "#F7F7F7",
        borderRadius: "8px",
        border: "1px solid #E6E6E6",
        overflow: "hidden",
        "&:before, &:after": { display: "none" },
        minHeight: 36,
        "& .MuiInputBase-input": {
            padding: "8px 12px",
            fontSize: "14px",
            lineHeight: "20px",
            color: "#222222",
            "&::placeholder": {
                color: "#6b7280",
                opacity: 1,
            },
        },
        "&.Mui-focused": {
            borderColor: "#BDBDBD",
            boxShadow: "none",
            backgroundColor: "#F7F7F7",
        },
        "&.Mui-disabled": {
            backgroundColor: "#F5F5F5",
            borderColor: "#E0E0E0",
            "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "#9E9E9E",
            },
        },
    },

  "& .MuiSelect-select": {
    color: "#6b7280",
    "&:not([aria-expanded])": {
      color: "#222222",
    },
  },
    "& .MuiInputLabel-root": {
        color: "#737373",
        fontWeight: 400,
        fontSize: "14px",
        lineHeight: "21px",
        letterSpacing: "0.2px",
        marginBottom: "6px",
        transform: "none",
        position: "relative",
    },
    "& .MuiInputLabel-shrink": {
        transform: "none",
    },

    "& .MuiFormHelperText-root": {
        marginTop: "6px",
        fontSize: "12px",
    },
};

const PartnerregionMultiplierModal: React.FC<Props> = ({
    onClose,
    onSubmit,
    open = true,
    mode = "add",
    initialValue,
    busy = false,
    countries = [
        { label: "USA", value: "USA" },
        { label: "North America", value: "North America" },
        { label: "Africa", value: "Africa" },
        { label: "Japan", value: "Japan" },
    ],
    regions = [
        { label: "Asia Pacific", value: "Asia Pacific" },
        { label: "North America", value: "North America" },
        { label: "Latin America and Caribbean", value: "Latin America and Caribbean" },
        { label: "Europe, Middle East, Africa", value: "Europe, Middle East, Africa" },
    ],
    codes = [
        { label: "APAC", value: "APAC" },
        { label: "NAM", value: "NAM" },
        { label: "LAM", value: "LAM" },
        { label: "EMEA", value: "EMEA" },
    ],
}) => {
    const [form, setForm] = React.useState<AddPartnerRegionalMultiplierDetailsType>({
        ID: initialValue?.ID ?? 0,
        country: initialValue?.country ?? "",
        region: initialValue?.region ?? "",
        region_code: initialValue?.region_code ?? "",
        value: Number(initialValue?.value ?? 1.0),
    });
    const { countryOptions } = usePartnerForm();
    

    const [multiplierInput, setMultiplierInput] = React.useState<string>(
        (initialValue?.value ?? 1.0).toFixed(1)
    );
    const [submitted, setSubmitted] = React.useState<boolean>(false);
    const [errors, setErrors] = React.useState<Record<string, string>>({});

    const {

        status: {
            addpartnerRegionalListLoading,
            updatepartnerRegionalListLoading,
            updatepartnerRegionalListStatus,
            updatepartnerRegionalListMessage,
            addpartnerRegionalListMessage,
            addpartnerRegionalListStatus
        } } = useAppSelector(
            (state) => state.partner_regionalmultiplier
        );

    React.useEffect(() => {
        if (!open) return;
        const v = Number(initialValue?.value ?? 1.0)
        setForm({
            ID: initialValue?.ID ?? 0,
            country: initialValue?.country ?? "",
            region: initialValue?.region ?? "",
            region_code: initialValue?.region_code ?? "",
            value: v,
        });
        setMultiplierInput(v.toFixed(1))
        setErrors({});
    }, [open, initialValue]);

    const validate = () => {
        const e: Record<string, string> = {};

        if (!form.country.trim()) {
            e.country_name = "Country Name is required";
        }

        if (!form.region.trim()) {
            e.region_name = "Region is required";
        }

        if (!form.region_code.trim()) {
            e.region_code = "Region Code is required";
        }

        const mult = Number(form.value);
        if (!Number.isFinite(mult)) {
            e.multiplier_value = "Multiplier Value must be a number";
        } else if (mult < 0) {
            e.multiplier_value = "Multiplier Value cannot be negative";
        }

        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = () => {
        if (!validate()) return;
        setSubmitted(true);
        const payload: AddPartnerRegionalMultiplierDetailsType = {
            ID: form.ID,
            country: form.country.trim(),
            region: form.region.trim(),
            region_code: form.region_code.trim(),
            value: Number(form.value),
        };

        onSubmit(payload);
    };
    const title = mode === "add" ? "Add New Modifier" : "Edit Modifier";
    const subtitle = "Configure a new modifier";
    const ctaLabel = mode === "add" ? "ADD Modifier" : "SAVE CHANGES";

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    boxShadow: "0px 12px 24px rgba(0,0,0,0.12)",
                    overflow: "hidden",
                },
            }}>
            {(!submitted || addpartnerRegionalListLoading || updatepartnerRegionalListLoading) &&
                <DialogTitle
                    sx={{
                        pb: 0,
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                    }}
                 className="pdttitle"
                >
                    <Box sx={{ pr: 1 }}>
                        <Typography
                            sx={{
                                width: "436px",
                                height: "30px",
                                fontFamily: "'Roboto'",
                                fontStyle: "normal",
                                fontWeight: 400,
                                fontSize: "26px",
                                lineHeight: "30px",
                                letterSpacing: "0.2px",
                                color: "#222222",
                                mt: 1.5,
                            }}
                        >
                            {title}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                width: "452px",
                                height: "21px",
                                fontFamily: "'Roboto'",
                                fontStyle: "normal",
                                fontWeight: 400,
                                fontSize: "14px",
                                lineHeight: "21px",
                                letterSpacing: "0.2px",
                                color: "#737373",
                                mb: 1.25,
                            }}
                        >
                            {subtitle}
                        </Typography>
                    </Box>

                    <IconButton aria-label="Close" onClick={onClose} sx={{ mt: 0.5 }}>
                        <Iconography icon="xmark" />
                    </IconButton>
                </DialogTitle>
            }

            {submitted ? (
                <DialogContent
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 2,
                    }}
                    style={{ padding: "24px" }}
                >
                    {(addpartnerRegionalListLoading || updatepartnerRegionalListLoading) && (
                        <Loader variant={LoaderSizes.medium} />
                    )}
                    {mode === "add" && !addpartnerRegionalListLoading && (
                        <>
                            <Iconography
                                icon={addpartnerRegionalListStatus ? "circleCheck" : "circleXmark"}
                                size={IconographySizes.XLarge}
                                theme={
                                    addpartnerRegionalListStatus
                                        ? IconographyThemes.Success
                                        : IconographyThemes.Error
                                }
                            />
                            <Typography variant="body2">
                                {addpartnerRegionalListMessage ||
                                    (addpartnerRegionalListStatus
                                        ? "Regional Modifier added successfully"
                                        : "Failed to add regional modifier")}
                            </Typography>
                            <Button
                                label="Close"
                                onClick={onClose}
                                variant="primary"
                            />
                        </>
                    )}
                    {mode === "edit" && !updatepartnerRegionalListLoading && (
                        <>
                            <Iconography
                                icon={
                                    updatepartnerRegionalListStatus ? "circleCheck" : "circleXmark"
                                }
                                size={IconographySizes.XLarge}
                                theme={
                                    updatepartnerRegionalListStatus
                                        ? IconographyThemes.Success
                                        : IconographyThemes.Error
                                }
                            />
                            <Typography variant="body2">
                                {updatepartnerRegionalListMessage ||
                                    (updatepartnerRegionalListStatus
                                        ? "License updated successfully"
                                        : "Failed to update license")}
                            </Typography>
                            <Button
                                label="Close"
                                onClick={onClose}
                                variant="primary"
                            />
                        </>
                    )}
                </DialogContent>
            ) :

                <>
                    <DialogContent sx={{ pt: 2 }} className="p16">
                        <FormControl fullWidth variant="filled" sx={{ mt: 2, ...fieldSx }}>
                            <InputLabel id="country-name-label">Country Name</InputLabel>
                            <Select
                                labelId="country-name-label"
                                value={form.country}
                                onChange={(e) => setForm({ ...form, country: e.target.value as string })}
                                displayEmpty
                                renderValue={(selected) =>
                                    selected ? String(selected) : "Select Country"
                                }
                                error={!!errors.country_name}
                                inputProps={{ "aria-label": "Country Name" }}
                            >
                                {countryOptions.map((c) => (
                                    <MenuItem key={c.value} value={c.value}>
                                        {c.label}
                                    </MenuItem>
                                ))}
                            </Select>
                            {!!errors.country_name && (
                                <Typography color="error" variant="caption">
                                    {errors.country_name}
                                </Typography>
                            )}
                        </FormControl>

                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 2,
                                mb: 2,
                                mt: 2,
                            }}>
                            <FormControl fullWidth variant="filled" sx={fieldSx}>
                                <InputLabel id="region-name-label">Region</InputLabel>
                                <Select
                                    labelId="region-name-label"
                                    value={form.region}
                                    onChange={(e) => setForm({ ...form, region: e.target.value as string })}
                                    displayEmpty
                                    renderValue={(selected) => (selected ? String(selected) : "Select region")}
                                    error={!!errors.region_name}
                                    inputProps={{ "aria-label": "Region" }}
                                >
                                    {regions.map((r) => (
                                        <MenuItem key={r.value} value={r.value}>
                                            {r.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {!!errors.region_name && (
                                    <Typography color="error" variant="caption">
                                        {errors.region_name}
                                    </Typography>
                                )}
                            </FormControl>
                            <FormControl fullWidth variant="filled" sx={fieldSx}>
                                <InputLabel id="region-code-label">Region Code</InputLabel>
                                <Select
                                    labelId="region-code-label"
                                    value={form.region_code}
                                    onChange={(e) =>
                                        setForm({ ...form, region_code: e.target.value as string })
                                    }
                                    displayEmpty
                                    renderValue={(selected) => (selected ? String(selected) : "Select code")}
                                    error={!!errors.region_code}
                                    inputProps={{ "aria-label": "Region Code" }}>
                                    {codes.map((c) => (
                                        <MenuItem key={c.value} value={c.value}>
                                            {c.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {!!errors.region_code && (
                                    <Typography color="error" variant="caption">
                                        {errors.region_code}
                                    </Typography>
                                )}
                            </FormControl>
                        </Box>

                        <FormControl variant="filled" sx={fieldSx}>
                            <InputLabel htmlFor="multiplier-value">Multiplier Value</InputLabel>
                            <TextField
                                id="multiplier-value"
                                type="number"
variant="filled"
                                value={multiplierInput}
                                onChange={(e) => {
                                    const raw = e.target.value;
                                    setMultiplierInput(raw);
                                    const n = parseFloat(raw);
                                    setForm({ ...form, value: Number.isFinite(n) ? n : 0 });
                                    setErrors((prev) => ({ ...prev, multiplier_value: "" }));
                                }}
                                onBlur={() => {
                                    const n = Number.parseFloat(multiplierInput);
                                    if (Number.isFinite(n)) {
                                        const formatted = n.toFixed(1);
                                        setMultiplierInput(formatted);
                                        setForm((prev) => ({ ...prev, value: n }));
                                    } else {
                                        setMultiplierInput("1.0");
                                        setForm((prev) => ({ ...prev, value: 1.0 }));
                                    }
                                }}
                                placeholder="1.0"
                                InputProps={{
                                    disableUnderline: true,
                                    inputProps: { step: "0.1", "aria-label": "Multiplier Value", min: 0 },
                                }}
                                error={!!errors.multiplier_value}
                                helperText={errors.multiplier_value || ""}
                                fullWidth
                            />
                        </FormControl>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, py: 2 }}>
                        <Button
                            variant="secondary"
                            label="Cancel"
                            onClick={onClose}
                            disabled={busy}
                        />
                        <Button
                            variant="primary"
                            label={ctaLabel}
                            onClick={handleSubmit}
                            disabled={busy}
                            loading={busy}
                        />
                    </DialogActions>
                </>


            }


        </Dialog>
    );
};

export default PartnerregionMultiplierModal;